/* MTH sale-pop — rotates the floating "recent purchase" card.
   Data is a JSON array (already sanitized server-side) in a child <script>.
   The element renders one card at a time, cycling newest-first, and computes
   relative time on the client (page caching would make server-rendered times
   stale). Thumbnails are preloaded and each card waits for its image to decode
   before it animates in, so a card never appears before its picture. Pauses on
   hover/focus; respects reduced-motion. */

class MthSalePop extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;

    this.entries = this._readData();
    if (!this.entries.length) {
      this.remove();
      return;
    }

    // Preload every thumbnail up front (there's a multi-second delay before the
    // first card) so images are cached and cards never appear picture-less.
    this.entries.forEach((e) => {
      if (e.image) {
        const im = new Image();
        im.src = e.image;
      }
    });

    this.initialDelay = parseInt(this.dataset.initialDelay, 10) || 5000;
    this.visibleMs = parseInt(this.dataset.visible, 10) || 6000;
    this.gapMs = parseInt(this.dataset.gap, 10) || 8000;

    this.index = 0;
    this.paused = false;
    this.timer = null;

    this._buildShell();
    this.hidden = false;

    this._start(this.initialDelay);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
  }

  _readData() {
    const tag = this.querySelector('.mth-sale-pop__data');
    if (!tag) return [];
    try {
      const arr = JSON.parse(tag.textContent);
      return Array.isArray(arr) ? arr.filter((e) => e && e.title && e.url) : [];
    } catch (e) {
      return [];
    }
  }

  _buildShell() {
    const link = document.createElement('a');
    link.className = 'mth-sale-pop__card';
    link.setAttribute('data-state', 'out');

    const thumb = document.createElement('img');
    thumb.className = 'mth-sale-pop__thumb';
    thumb.alt = '';
    thumb.decoding = 'async';
    thumb.width = 56;
    thumb.height = 56;

    const body = document.createElement('div');
    body.className = 'mth-sale-pop__body';

    // Two text rows: the "who bought" line on top, the product title on its own
    // guaranteed single line below it (so the title never stacks or shifts).
    const who = document.createElement('div');
    who.className = 'mth-sale-pop__who';

    const title = document.createElement('div');
    title.className = 'mth-sale-pop__title';

    const time = document.createElement('div');
    time.className = 'mth-sale-pop__time';

    body.appendChild(who);
    body.appendChild(title);
    body.appendChild(time);
    link.appendChild(thumb);
    link.appendChild(body);

    // Pause while the visitor is engaging with the card.
    link.addEventListener('mouseenter', () => (this.paused = true));
    link.addEventListener('mouseleave', () => (this.paused = false));
    link.addEventListener('focusin', () => (this.paused = true));
    link.addEventListener('focusout', () => (this.paused = false));

    this._card = link;
    this._thumb = thumb;
    this._who = who;
    this._title = title;
    this._time = time;

    this.appendChild(link);
  }

  _start(delay) {
    this.timer = setTimeout(() => this._show(), delay);
  }

  async _show() {
    const entry = this.entries[this.index];
    this._render(entry);
    // Wait until the thumbnail is actually decodable before revealing, so the
    // card never flashes in and then swaps its image (preload usually makes
    // this resolve instantly).
    if (this._thumb.getAttribute('src')) {
      try {
        await this._thumb.decode();
      } catch (e) {}
    }
    // If the element was removed mid-decode (e.g. section re-render in the
    // theme editor), stop — otherwise we'd install a _hide timer that nothing
    // clears and keep rotating on a detached node.
    if (!this.isConnected) return;
    // next frame so the transition runs from the "out" state
    requestAnimationFrame(() =>
      requestAnimationFrame(() => this._card.setAttribute('data-state', 'in'))
    );
    this.timer = setTimeout(() => this._hide(), this.visibleMs);
  }

  _hide() {
    // Don't hide while the visitor is hovering/focused — check back shortly.
    if (this.paused) {
      this.timer = setTimeout(() => this._hide(), 800);
      return;
    }
    this._card.setAttribute('data-state', 'out');
    this.index = (this.index + 1) % this.entries.length;
    this.timer = setTimeout(() => this._show(), this.gapMs);
  }

  _render(entry) {
    this._card.href = entry.url;

    if (entry.image) {
      this._thumb.src = entry.image;
      this._thumb.style.display = '';
    } else {
      this._thumb.removeAttribute('src');
      this._thumb.style.display = 'none';
    }

    const place = entry.country
      ? entry.city + ', ' + entry.country
      : entry.city;

    // Top row: "Name from City bought" (text nodes + span, no innerHTML).
    this._who.textContent = '';
    const name = document.createElement('span');
    name.className = 'mth-sale-pop__name';
    name.textContent = entry.name || 'Someone';
    this._who.appendChild(name);
    this._who.appendChild(
      document.createTextNode(place ? ' from ' + place + ' bought' : ' bought')
    );

    // Bottom row: the product title, its own single line.
    this._title.textContent = entry.title;

    const timeStr = this._relativeTime(entry.at);
    this._time.textContent = timeStr;

    // Accessible summary for the whole card (screen readers). Only append the
    // time when we have one, so the label never ends in a dangling ", ".
    this._card.setAttribute(
      'aria-label',
      (entry.name || 'Someone') +
        (place ? ' from ' + place : '') +
        ' bought ' +
        entry.title +
        (timeStr ? ', ' + timeStr : '')
    );
  }

  _relativeTime(at) {
    // Accepts an ISO-8601 string or an epoch value (seconds, as number or
    // numeric string) — matches whatever Flow stored in the metafield.
    let then;
    if (typeof at === 'number') {
      then = at * 1000;
    } else if (typeof at === 'string' && /^\d+$/.test(at.trim())) {
      then = Number(at.trim()) * 1000;
    } else {
      then = Date.parse(at);
    }
    if (!then || isNaN(then)) return '';
    let diff = Math.floor((Date.now() - then) / 1000);
    if (diff < 0) diff = 0;

    if (diff < 60) return 'just now';
    const mins = Math.floor(diff / 60);
    if (mins < 60) return mins + (mins === 1 ? ' minute ago' : ' minutes ago');
    const hours = Math.floor(mins / 60);
    if (hours < 24) return hours + (hours === 1 ? ' hour ago' : ' hours ago');
    const days = Math.floor(hours / 24);
    if (days < 7) return days + (days === 1 ? ' day ago' : ' days ago');
    const weeks = Math.floor(days / 7);
    return weeks + (weeks === 1 ? ' week ago' : ' weeks ago');
  }
}

customElements.define('mth-sale-pop', MthSalePop);
